import React, { useCallback, useEffect, useState } from 'react';
import trfService from '../services/trfService';
import './TrfConfigPage.css';

export const validateTrfConfig = (cfg) => {
  const errors = [];
  if (cfg.printMode !== 'COMBINED' && cfg.printMode !== 'PER_TEST') {
    errors.push('Select a TRF printing mode.');
  }
  if (cfg.autoPrint && !cfg.isEnabled) {
    errors.push('Automatic printing cannot be on while TRF is disabled.');
  }
  return errors;
};

const MODES = [
  {
    value: 'COMBINED',
    title: 'One TRF for all Radiology tests',
    help: 'A registration with 4 Radiology tests prints 1 TRF listing all 4 tests.',
  },
  {
    value: 'PER_TEST',
    title: 'One TRF per Radiology test',
    help: 'A registration with 4 Radiology tests prints 4 TRFs, one for each test.',
  },
];

export const TrfConfigPage = () => {
  const [cfg, setCfg] = useState({ isEnabled: true, printMode: 'COMBINED', autoPrint: true });
  const [saved, setSaved] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await trfService.getConfig();
      if (res.success && res.data) {
        const next = {
          isEnabled: !!res.data.isEnabled,
          printMode: res.data.printMode,
          autoPrint: !!res.data.autoPrint,
        };
        setCfg(next);
        setSaved(next);
        setMeta({ updatedBy: res.data.updatedBy, updatedDate: res.data.updatedDate });
      } else {
        setMessage({ type: 'error', text: res.message || 'Failed to load TRF configuration.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to load TRF configuration.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const errors = validateTrfConfig(cfg);
  const dirty = saved && JSON.stringify(saved) !== JSON.stringify(cfg);

  const update = (patch) => {
    setMessage(null);
    setCfg((prev) => {
      const next = { ...prev, ...patch };
      // Turning TRF off also turns auto-print off so the pair can never be contradictory.
      if (patch.isEnabled === false) next.autoPrint = false;
      return next;
    });
  };

  const handleSave = async () => {
    if (errors.length || saving) return;
    setSaving(true);
    setMessage(null);
    try {
      const res = await trfService.saveConfig(cfg);
      if (res.success) {
        setMessage({ type: 'success', text: 'TRF configuration saved.' });
        await load();
      } else {
        setMessage({ type: 'error', text: res.message || 'Failed to save TRF configuration.' });
      }
    } catch (err) {
      const details = err.response?.data?.errors;
      setMessage({
        type: 'error',
        text: [err.response?.data?.message || 'Failed to save TRF configuration.', ...(Array.isArray(details) ? details : [])].join(' '),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="trfcfg-container">
      <header className="trfcfg-header">
        <h1>TRF Configuration</h1>
        <p>Test Requisition Forms are generated only for Radiology tests of a saved registration.</p>
      </header>

      {message && (
        <div className={`trfcfg-msg trfcfg-msg--${message.type}`} role={message.type === 'error' ? 'alert' : 'status'}>
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="trfcfg-card">Loading…</div>
      ) : (
        <div className="trfcfg-card">
          <label className="trfcfg-row">
            <input
              type="checkbox"
              checked={cfg.isEnabled}
              onChange={(e) => update({ isEnabled: e.target.checked })}
            />
            <span>
              <strong>Enable TRF printing</strong>
              <small>When off, no TRF is generated or printed, including from the receipt screen.</small>
            </span>
          </label>

          <fieldset className="trfcfg-modes" disabled={!cfg.isEnabled}>
            <legend>Printing mode</legend>
            {MODES.map((m) => (
              <label key={m.value} className={`trfcfg-mode ${cfg.printMode === m.value ? 'is-selected' : ''}`}>
                <input
                  type="radio"
                  name="trf-print-mode"
                  value={m.value}
                  checked={cfg.printMode === m.value}
                  onChange={() => update({ printMode: m.value })}
                />
                <span>
                  <strong>{m.title}</strong>
                  <small>{m.help}</small>
                </span>
              </label>
            ))}
          </fieldset>

          <label className="trfcfg-row">
            <input
              type="checkbox"
              checked={cfg.autoPrint}
              disabled={!cfg.isEnabled}
              onChange={(e) => update({ autoPrint: e.target.checked })}
            />
            <span>
              <strong>Print automatically after registration is saved</strong>
              <small>The Print TRF button on the receipt screen is always available for manual (re)print while TRF is enabled.</small>
            </span>
          </label>

          {errors.length > 0 && (
            <ul className="trfcfg-errors" role="alert">
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          )}

          <div className="trfcfg-actions">
            <button type="button" className="trfcfg-save" onClick={handleSave} disabled={saving || !dirty || errors.length > 0}>
              {saving ? 'Saving…' : 'Save Configuration'}
            </button>
            {meta?.updatedBy && (
              <span className="trfcfg-meta">
                Last changed by {meta.updatedBy}
                {meta.updatedDate ? ` on ${new Date(meta.updatedDate).toLocaleString()}` : ''}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TrfConfigPage;
